import { Fragment, useMemo } from "react";
import { ToggleGroup, ToggleGroupItem, ToggleGroupSeparator } from "@/components/ui/toggle-group";
import type { RelativeDuration } from "../ChartSpec";
import { useDataViewerStore } from "../data-viewer-provider";
import { PRESET_DURATION_MS } from "./time-range-slice";

/** UX subset for this picker — not every RelativeDuration gets a button. */
const PICKER_PRESETS = ["30d", "7d", "1d", "1h"] as const satisfies readonly RelativeDuration[];
const PICKER_PRESET_SET = new Set<RelativeDuration>(PICKER_PRESETS);

/** Default presets plus the active value when it falls outside the subset (e.g. spec-seeded "6h"). */
function pickerPresetsForActive(active: RelativeDuration | null): RelativeDuration[] {
    if (!active || PICKER_PRESET_SET.has(active)) {
        return [...PICKER_PRESETS];
    }
    return [...PICKER_PRESETS, active].sort((a, b) => PRESET_DURATION_MS[b] - PRESET_DURATION_MS[a]);
}

export function WindowRangePicker() {
    const preset = useDataViewerStore((state) => state.preset);
    const setPreset = useDataViewerStore((state) => state.setPreset);
    const options = useMemo(() => pickerPresetsForActive(preset), [preset]);

    return (
        <ToggleGroup
            value={preset ? [preset] : []}
            variant={"outline"}
            onValueChange={(value) => {
                const next = value[0];
                if (next) {
                    setPreset(next as RelativeDuration);
                }
            }}
        >
            {options.map((option, index) => (
                <Fragment key={option}>
                    <ToggleGroupItem aria-label={`${option} range`} value={option}>
                        {option}
                    </ToggleGroupItem>
                    {index < options.length - 1 && <ToggleGroupSeparator key={`${option}-separator`} />}
                </Fragment>
            ))}
        </ToggleGroup>
    );
}
